import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import FormFields from "../../components/common/FormFields";
import { SLOT } from "../../utils/constants";
import { IFormSection, TFormHookParams } from "../../types/common";
import {
  createColumn,
  createEditAction,
  getForm,
  getRegister,
  getResultOptions,
} from "../../utils/helpers";
import { URL_SLOTS } from "../../utils/urls";
import useGet from "../common/useGet";
import { API_SLOTS, API_PARKINGS } from "../../utils/apis";

const resolveParkingName = (parkingId: any, parkingsArray: any[]) => {
  if (!parkingId || !parkingsArray || !Array.isArray(parkingsArray)) {
    return "Unknown Parking";
  }

  const parking = parkingsArray.find((p) => p.id === parkingId);
  return parking ? parking.name : "Unknown Parking";
};

export const useSlots = () => {
  const {
    loadData: loadParkingData,
    loading: loadingParkings,
    data: parkingsData,
  } = useGet();

  // Load parking data when hook is called
  useEffect(() => {
    loadParkingData({ api: API_PARKINGS });
  }, []);

  const tableColumns = () => [
    createColumn("district_name", true, "District Name"),
    createColumn("name", true, "Slot Number"),
    createColumn("parking_name", true),
    createColumn("is_available", false),
    createColumn("actions", false),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      return {
        ...item,
        parking_name: resolveParkingName(item?.parking, parkingsData?.results),
        is_available: item?.is_available ? "Available" : "Occupied",
        actions: (
          <FormActions actions={[createEditAction(URL_SLOTS, item?.id)]} />
        ),
      };
    });
  };

  const formRoute = getForm(URL_SLOTS);
  const activityFilter = "slots";

  return {
    tableData,
    tableColumns,
    formRoute,
    parkingsData,
    activityFilter,
    loadingParkings,
  };
};

export const useSlotForm = ({ updateFormValues }: TFormHookParams = {}) => {
  const {
    loadData: loadParkingData,
    loading: loadingParkings,
    data: parkingsData,
  } = useGet();

  const { loadData, loading, data } = useGet();

  useEffect(() => {
    loadData({ api: API_SLOTS });
  }, []);

  useEffect(() => {
    loadParkingData({ api: API_PARKINGS });
  }, []);

  useEffect(() => {
    if (updateFormValues) {
      updateFormValues({
        street_type: SLOT,
      });
    }
  }, [updateFormValues]);

  const sections: IFormSection[] = [
    {
      title: "Slot Details",
      fields: [
        {
          field_name: "name",
          label: "Slot Number",
          type: "text",
          props: { required: true },
        },
        {
          field_name: "parking",
          label: "Parking",
          type: "select",
          props: { required: true },
          options_data: getResultOptions(parkingsData, "name"),
          loading: loadingParkings,
        },
        {
          field_name: "is_available",
          label: "Is Available",
          type: "select",
          props: { required: false },
          options_data: [
            { id: true, name: "Available" },
            { id: false, name: "Occupied" },
          ],
        },
      ],
    },
    {
      title: "Location Details",
      fields: [
        {
          field_name: "coordinates",
          type: "location",
          props: { wide: true },
        },
      ],
    },
  ];

  const getConfigOptions = () => {
    return {
      parkings: getResultOptions(parkingsData, "name"),
    };
  };

  const options = {
    ...getConfigOptions(),
  };

  const title = "Slot Management";
  const redirect = getRegister(URL_SLOTS);

  return {
    sections,
    title,
    redirect,
    options,
    loading,
    data,
    parkingsData,
    loadingParkings,
  };
};

export const SlotForm = ({ form, error, updateFormValue, onChange }: any) => {
  const { sections } = useSlotForm({
    form,
    updateFormValues: updateFormValue,
  });

  return (
    <div>
      {sections.map((section: IFormSection, index: number) => (
        <div key={index}>
          <h3>{section.title}</h3>
          <FormFields
            form={form}
            error={error}
            updateFormValue={updateFormValue}
            onChange={onChange}
            fields={section.fields}
          />
        </div>
      ))}
    </div>
  );
};
